import React from 'react';
import { 
    Typography, 
    Container, 
    Paper, 
    Box,
    Divider,
    Button
} from '@mui/material';
import { Link } from 'react-router-dom';
import SEO from '../SEO';

const ProtocolAssistant = () => {
    const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'BlogPosting',
        headline: 'Ask Your Protocol: How PCRAssist.com is Changing EMS Education and Compliance',
        description: 'Explore how our Protocol Assistant helps streamline PCR protocol development, making your molecular biology workflow more efficient and reliable.',
        image: '%PUBLIC_URL%/images/simple-medical-ai-logo.svg',
        author: {
            '@type': 'Organization',
            name: 'PCRAssist',
            url: 'https://pcrassist.com'
        },
        publisher: {
            '@type': 'Organization',
            name: 'PCRAssist',
            url: 'https://pcrassist.com',
            logo: {
                '@type': 'ImageObject',
                url: 'https://pcrassist.com/images/simple-medical-ai-logo.svg'
            }
        },
        datePublished: '2025-06-04',
        dateModified: '2025-06-08',
        mainEntityOfPage: {
            '@type': 'WebPage',
            '@id': 'https://pcrassist.com/blog/protocol-assistant'
        }
    };

    return (
        <>
            <SEO 
                title="Protocol Assistant - PCRAssist"
                description="Explore how our Protocol Assistant helps streamline PCR protocol development, making your molecular biology workflow more efficient and reliable."
                canonicalUrl="/blog/protocol-assistant"
                type="article"
                publishedTime="2025-06-04"
                modifiedTime="2025-06-08"
                jsonLd={jsonLd}
            />
            <Container maxWidth={false} sx={{ py: 4, maxWidth: '1200px', margin: '0 auto' }}>
                <Paper elevation={3} sx={{ p: 6, borderRadius: 3, background: 'linear-gradient(145deg, #ffffff 0%, #f8fafc 100%)' }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{
                        color: '#1f2937',
                        fontWeight: 800,
                        mb: 4,
                        background: 'linear-gradient(135deg, #1f2937 0%, #2563eb 100%)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text'
                    }}>
                        Ask Your Protocol: How PCRAssist.com is Changing EMS Education and Compliance
                    </Typography>

                    <Typography paragraph sx={{ fontSize: '1.1rem', lineHeight: 1.7, color: '#374151', fontStyle: 'italic', mb: 3 }}>
                        What if you could upload your EMS protocol and ask it questions like a teammate?
                        That's exactly what PCRAssist.com/protocol offers.
                    </Typography>

                    <Typography paragraph sx={{ fontSize: '1rem', lineHeight: 1.6, color: '#4b5563' }}>
                        At PCRAssist, we understand the challenges medics face tracking constantly changing protocols
                        and agency-specific variations. Our Protocol Assistant provides AI-powered, searchable,
                        conversational protocols for medics and educators.
                    </Typography>

                    <Box sx={{ my: 4, p: 3, backgroundColor: '#f0f9ff', borderRadius: 2, borderLeft: '4px solid #2563eb' }}>
                        <Typography variant="h6" gutterBottom sx={{ color: '#1f2937', fontWeight: 700 }}>
                            📥 Upload. 💬 Ask. ✅ Understand.
                        </Typography>
                        <Typography component="ul" sx={{ pl: 0, listStyleType: 'none', mb: 2 }}>
                            <Typography component="li" sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span>Upload your EMS protocols (PDF, DOCX, or text)</span>
                            </Typography>
                            <Typography component="li" sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span>Let our system process and understand them</span>
                            </Typography>
                            <Typography component="li" sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 2
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span>Ask questions in plain English like:</span>
                            </Typography>
                        </Typography>
                        <Box sx={{ pl: 3, borderLeft: 2, borderColor: '#93c5fd', ml: 2 }}>
                            <Typography sx={{ fontStyle: 'italic', color: '#6b7280', fontSize: '0.95rem', mb: 1 }}>
                                "What's the dose of ketamine for excited delirium?"
                            </Typography>
                            <Typography sx={{ fontStyle: 'italic', color: '#6b7280', fontSize: '0.95rem', mb: 1 }}>
                                "Can I skip spinal precautions if the patient is alert?"
                            </Typography>
                            <Typography sx={{ fontStyle: 'italic', color: '#6b7280', fontSize: '0.95rem' }}>
                                "What's our pediatric bradycardia algorithm?"
                            </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '1rem', lineHeight: 1.6, color: '#4b5563', mt: 2 }}>
                            The assistant responds instantly, citing your protocol and helping you stay accurate and confident.
                        </Typography>
                    </Box>

                    <Box sx={{ my: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ color: '#1f2937', fontWeight: 700, mb: 3 }}>
                            🚑 Why Use the Protocol Assistant?
                        </Typography>

                        <Typography component="ul" sx={{ pl: 0, listStyleType: 'none' }}>
                            <Typography component="li" paragraph sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 3
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span><strong>No more hunting through PDFs:</strong> Forget scrolling through 100-page manuals on your phone at 2am. Just ask.</span>
                            </Typography>

                            <Typography component="li" paragraph sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 3
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span><strong>Better training and onboarding:</strong> New hires can quiz the protocol instead of flipping through outdated binders. It's like having an experienced FTO in your pocket.</span>
                            </Typography>

                            <Typography component="li" paragraph sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 3
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span><strong>Retention through repetition:</strong> The more you ask, the more you retain. Medics can use the tool for ongoing self-study and scenario prep.</span>
                            </Typography>

                            <Typography component="li" paragraph sx={{
                                fontSize: '1rem',
                                lineHeight: 1.6,
                                color: '#4b5563',
                                display: 'flex',
                                alignItems: 'flex-start',
                                mb: 3
                            }}>
                                <span style={{ color: '#2563eb', fontWeight: 'bold', marginRight: '12px', fontSize: '1.2rem' }}>•</span>
                                <span><strong>Stay compliant and up-to-date:</strong> Protocols change. Our assistant makes it easy to compare versions and clarify policy—even between agencies.</span>
                            </Typography>
                        </Typography>
                    </Box>

                    <Box sx={{ my: 4, p: 3, backgroundColor: '#f8fafc', borderRadius: 2, borderLeft: '4px solid #2563eb' }}>
                        <Typography paragraph sx={{ fontSize: '1rem', lineHeight: 1.6, color: '#4b5563', mb: 0 }}>
                            <strong>👨‍⚕️ Built for Medics, by Medics:</strong> PCRAssist is already trusted for writing better Patient Care Reports.
                            Now we're bringing the same AI-powered clarity to protocol documents. Whether you're a field medic,
                            EMS educator, or medical director, this tool saves time, reduces uncertainty, and keeps care aligned with standards.
                        </Typography>
                    </Box>

                    <Box sx={{ my: 4, textAlign: 'center', bgcolor: 'action.hover', p: 4, borderRadius: 2 }}>
                        <Typography variant="h5" gutterBottom>
                            💬 Try It Now
                        </Typography>
                        <Typography paragraph>
                            Upload your protocol today and start asking questions
                        </Typography>
                        <Button 
                            variant="contained" 
                            size="large" 
                            component={Link} 
                            to="/protocol"
                            sx={{ mt: 2 }}
                        >
                            👉 Try Protocol Assistant
                        </Button>
                        <Typography variant="h6" sx={{ mt: 3 }}>
                            It's EMS intelligence—on demand.
                        </Typography>
                    </Box>

                    <Box sx={{ mt: 4, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="body2" color="text.secondary">
                            © 2025 PCRAssist. All rights reserved.
                        </Typography>
                        <Link to="/blog" style={{ textDecoration: 'none' }}>
                            <Typography color="primary">← Back to Blog</Typography>
                        </Link>
                    </Box>
                </Paper>
            </Container>
        </>
    );
};

export default ProtocolAssistant; 